import type { CurriculumModuleOption } from '../../model/assignments/interfaces'
import type { DisciplineCode } from '../../model/assignments/types'
import type { ApiCallbacks, ApiResponse } from '@shared/api'
import type { Grade } from '../../model/assignments/enums'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { CurriculumModules } from '../../lib/assignments/schema'

interface CurriculumModulesApiOptions extends ApiCallbacks<CurriculumModuleOption[]> {
  disciplineCode: DisciplineCode
  grade: Grade
}

export const useCurriculumModulesApi = async (
  options: CurriculumModulesApiOptions,
): Promise<CurriculumModuleOption[] | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const url = useAdminRoutes('admin.curriculum.modules.index', {
    discipline_code: options.disciplineCode,
    grade: options.grade,
  })

  options.onStart?.()

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url)

    if (!data.success) {
      toast.error(data.message)
      options.onError?.(data.message)
      return null
    }

    const raw: unknown = data.data?.modules

    // A `200` with no array is a broken contract, not an empty catalogue.
    if (!Array.isArray(raw)) {
      const msg = 'Unexpected error. The curriculum modules are missing from the response.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    // So is an array whose modules lack `topics` or `lessons` — the pickers read both unguarded.
    const parsed = CurriculumModules.safeParse(raw)

    if (!parsed.success) {
      const msg = 'Unexpected error. The curriculum modules in the response are malformed.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    const modules: CurriculumModuleOption[] = parsed.data

    options.onSuccess?.(modules)
    return modules
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error(msg)
    options.onError?.(msg)
    return null
  } finally {
    options.onFinish?.()
  }
}
